import React, { useState, useEffect } from 'react';
import { Sparkles, Download, Check, Send, Edit2, X, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { hasPI } from '../../../lib/permissions';
import { piApi } from '../../../services/piApi';
import '../PIShell.css';

const STATUS_CONFIG = {
  draft:    { label: 'Draft',                    cls: 'pi-badge-blue'  },
  approved: { label: 'Approved — ready to send', cls: 'pi-badge-amber' },
  sent:     { label: 'Sent',                     cls: 'pi-badge-green' },
};

const QUARTERS = ['Q1','Q2','Q3','Q4'];
const YEARS = [2024, 2025, 2026, 2027];

export default function PILPReportingTab({ portco, currentUser }) {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(`Q${Math.ceil((new Date().getMonth() + 1) / 3)} ${new Date().getFullYear()}`);
  const [viewingReport, setViewingReport] = useState(null);
  const [editingNarrative, setEditingNarrative] = useState(null);
  const [editedText, setEditedText] = useState('');
  const [approving, setApproving] = useState(null);
  const [sending, setSending] = useState(null);
  const [sentConfirm, setSentConfirm] = useState(null);
  const [completenessWarnings, setCompletenessWarnings] = useState(null);
  const [checkingCompleteness, setCheckingCompleteness] = useState(false);

  const canCreate  = hasPI(currentUser, 'pi.lp_reports.create');
  const canApprove = hasPI(currentUser, 'pi.lp_reports.approve');
  const canSend    = hasPI(currentUser, 'pi.lp_reports.send');

  const load = () => {
    piApi.getLPReportsForPortco(portco.id).then(r => setReports(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [portco.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const runCompletenessCheck = async () => {
    const warnings = [];
    try {
      const [initRes, bridgeRes, returnsRes] = await Promise.all([
        piApi.getInitiatives(portco.id),
        piApi.getBridgeItems(portco.id),
        piApi.getReturnScenarios(portco.id),
      ]);
      const initiatives = initRes.data || [];
      const bridgeItems = bridgeRes.data || [];
      const scenarios = returnsRes.data || [];

      if (!initiatives.some(i => i.status === 'in_progress' || i.status === 'complete')) {
        warnings.push({ msg: 'No active value creation initiatives. Paragraph 2 will have limited content.', tab: 'vcp' });
      }
      if (!bridgeItems.some(b => b.actual_value != null)) {
        warnings.push({ msg: 'No EBITDA actuals entered. Paragraph 1 performance summary will be limited.', tab: 'bridge' });
      }
      const base = scenarios.find(s => s.scenario === 'base');
      if (!base || base.exit_ebitda == null) {
        warnings.push({ msg: 'No exit scenario saved. Return trajectory context will be omitted from the report.', tab: 'returns' });
      }
      if (portco.entry_ebitda == null || portco.close_date == null) {
        warnings.push({ msg: 'Entry EBITDA or close date is missing. The performance summary cannot be benchmarked against entry assumptions.', tab: 'overview' });
      }
    } catch (e) {
      // If checks fail, proceed without warnings
    }
    return warnings;
  };

  const handleGenerateClick = async () => {
    const existingForPeriod = reports.find(r => r.period === selectedPeriod);
    if (existingForPeriod) {
      doGenerate();
      return;
    }
    setCheckingCompleteness(true);
    const warnings = await runCompletenessCheck();
    setCheckingCompleteness(false);
    if (warnings.length === 0) {
      doGenerate();
    } else {
      setCompletenessWarnings(warnings);
    }
  };

  const doGenerate = async () => {
    setCompletenessWarnings(null);
    setGenerating(true);
    try {
      await piApi.generateLPReport(portco.id, selectedPeriod);
      load();
    } catch (e) { console.error('[LP] Generate error:', e); }
    finally { setGenerating(false); }
  };

  const handleApprove = async (reportId) => {
    setApproving(reportId);
    try {
      await piApi.approveLPReport(portco.id, reportId);
      load();
    } catch (e) { console.error('[LP] Approve error:', e); }
    finally { setApproving(null); }
  };

  const handleSend = async (reportId) => {
    setSending(reportId);
    try {
      await piApi.sendLPReport(portco.id, reportId);
      setSentConfirm(reportId);
      setTimeout(() => setSentConfirm(null), 4000);
      load();
    } catch (e) { console.error('[LP] Send error:', e); }
    finally { setSending(null); }
  };

  const handleDownloadPDF = (report) => {
    const w = window.open('', '_blank');
    const cfg = STATUS_CONFIG[report.status] || STATUS_CONFIG.draft;
    w.document.write(`
      <html>
        <head>
          <title>${portco.name} — ${report.period} LP Report</title>
          <style>
            body { font-family: Georgia, serif; max-width: 720px; margin: 60px auto; color: #1e293b; line-height: 1.75; }
            h1 { font-size: 22px; margin-bottom: 4px; }
            .meta { font-size: 13px; color: #64748b; margin-bottom: 32px; }
            .status { display: inline-block; padding: 2px 10px; border-radius: 20px; font-size: 12px; font-weight: 600; background: #e0f2fe; color: #0369a1; }
            .narrative { font-size: 15px; white-space: pre-wrap; }
            .footer { margin-top: 48px; font-size: 11px; color: #94a3b8; border-top: 1px solid #e2e8f0; padding-top: 12px; }
          </style>
        </head>
        <body>
          <h1>${portco.name}</h1>
          <div class="meta">
            ${report.period} LP Report &nbsp;·&nbsp;
            <span class="status">${cfg.label}</span> &nbsp;·&nbsp;
            Generated ${new Date(report.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </div>
          <div class="narrative">${report.narrative || 'No content.'}</div>
          <div class="footer">Generated by DealNexa Portfolio Intelligence &nbsp;·&nbsp; Confidential</div>
        </body>
      </html>
    `);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 400);
  };

  const handleSaveEdit = async (reportId) => {
    await piApi.updateLPReport(portco.id, reportId, { narrative: editedText }).catch(() => {});
    setEditingNarrative(null);
    setEditedText('');
    load();
  };

  const PeriodSelector = () => (
    <select value={selectedPeriod} onChange={e => setSelectedPeriod(e.target.value)}
      style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, color: '#334155' }}>
      {YEARS.flatMap(y => QUARTERS.map(q => <option key={`${q} ${y}`}>{q} {y}</option>))}
    </select>
  );

  const allChecksFailed = completenessWarnings && completenessWarnings.length === 4;

  return (
    <div style={{ padding: '24px 28px' }}>
      {/* Generate control */}
      {canCreate && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 20 }}>
          <PeriodSelector />
          <button className="pi-ai-btn" onClick={handleGenerateClick} disabled={generating || checkingCompleteness} aria-label="Generate LP report">
            <Sparkles size={13} />
            {checkingCompleteness ? 'Checking data...' : generating ? 'Drafting LP report...' : 'Generate Report'}
          </button>
        </div>
      )}

      {/* Data completeness warning */}
      {completenessWarnings && completenessWarnings.length > 0 && (
        <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8, padding: '14px 18px', marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
            <AlertTriangle size={14} style={{ color: '#92400e' }} />
            <span style={{ fontSize: 14, fontWeight: 600, color: '#92400e' }}>Some data is missing</span>
          </div>
          {completenessWarnings.map((w, i) => (
            <div key={i} style={{ fontSize: 13, color: '#78716c', marginBottom: 4, paddingLeft: 22 }}>{w.msg}</div>
          ))}
          {allChecksFailed && (
            <div style={{ fontSize: 13, color: '#92400e', fontWeight: 500, margin: '10px 0 6px', paddingLeft: 22 }}>
              This report would contain almost no portco-specific content. We recommend entering data first.
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 12, paddingLeft: 22 }}>
            {allChecksFailed ? (
              <>
                <button className="pi-btn-primary" style={{ fontSize: 12 }}
                  onClick={() => { setCompletenessWarnings(null); navigate(`/pi/portco/${portco.id}/${completenessWarnings[0].tab}`); }}>
                  Go Fill in Data
                </button>
                <button className="pi-btn-ghost" style={{ fontSize: 12 }} onClick={doGenerate}>
                  Generate Anyway
                </button>
              </>
            ) : (
              <>
                <button className="pi-btn-primary" style={{ fontSize: 12 }} onClick={doGenerate}>
                  Generate Anyway
                </button>
                <button className="pi-btn-ghost" style={{ fontSize: 12 }}
                  onClick={() => { setCompletenessWarnings(null); navigate(`/pi/portco/${portco.id}/${completenessWarnings[0].tab}`); }}>
                  Go Fill in Data
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {generating && <div className="pi-loading" style={{ marginBottom: 16 }}><div className="pi-spinner" /><span>Drafting LP report...</span></div>}

      {/* Sent confirmation */}
      {sentConfirm && (
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: 13, color: '#166534' }}>
          Report marked as sent. Your LP will receive this through your normal distribution channel.
        </div>
      )}

      {/* Reports table */}
      <div className="pi-card">
        <div className="pi-card-header"><span className="pi-card-title">Reports</span></div>
        {loading ? (
          <div className="pi-loading" style={{ padding: 20 }}><div className="pi-spinner" /></div>
        ) : reports.length === 0 ? (
          <div className="pi-empty" style={{ padding: 32 }}>
            <div className="pi-empty-title">No reports generated yet</div>
            <div className="pi-empty-sub">Generate your first LP report.</div>
            {canCreate && (
              <button className="pi-ai-btn" onClick={handleGenerateClick} disabled={generating || checkingCompleteness}>
                <Sparkles size={13} /> Generate First Report
              </button>
            )}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr style={{ background: '#fafbff', borderBottom: '1px solid #f1f5f9' }}>
                {['Period', 'Generated', 'Status', 'Generated By', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '9px 14px', textAlign: 'left', color: '#64748b', fontWeight: 500 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {reports.map(r => {
                const cfg = STATUS_CONFIG[r.status] || STATUS_CONFIG.draft;
                return (
                  <tr key={r.id} style={{ borderBottom: '1px solid #f8fafc' }}>
                    <td style={{ padding: '11px 14px', fontWeight: 600, color: '#334155' }}>{r.period}</td>
                    <td style={{ padding: '11px 14px', color: '#64748b' }}>
                      {new Date(r.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </td>
                    <td style={{ padding: '11px 14px' }}>
                      <span className={`pi-badge ${cfg.cls}`} aria-label={`Status: ${cfg.label}`}>{cfg.label}</span>
                    </td>
                    <td style={{ padding: '11px 14px', color: '#64748b' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        {r.generated_by === 'ai' ? <><Sparkles size={12} style={{ color: '#4f46e5' }} /> AI draft</> : 'Manual'}
                      </span>
                    </td>
                    <td style={{ padding: '11px 14px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="pi-btn-ghost" style={{ fontSize: 11, padding: '4px 8px' }}
                          onClick={() => setViewingReport(viewingReport?.id === r.id ? null : r)}
                          aria-label={`${viewingReport?.id === r.id ? 'Hide' : 'View'} report`}>
                          {viewingReport?.id === r.id ? <X size={12} /> : 'View'}
                        </button>
                        {r.status === 'draft' && canApprove && (
                          <button className="pi-btn-ghost" style={{ fontSize: 11, padding: '4px 8px' }}
                            onClick={() => handleApprove(r.id)} disabled={approving === r.id}
                            aria-label="Approve report">
                            <Check size={12} /> {approving === r.id ? '…' : 'Approve'}
                          </button>
                        )}
                        {r.status === 'approved' && canSend && (
                          <button className="pi-ai-btn" style={{ fontSize: 11, padding: '4px 8px' }}
                            onClick={() => handleSend(r.id)} disabled={sending === r.id}
                            aria-label="Send report to LPs">
                            <Send size={12} /> {sending === r.id ? 'Sending…' : 'Send'}
                          </button>
                        )}
                        <button className="pi-btn-ghost" style={{ fontSize: 11, padding: '4px 8px' }}
                          onClick={() => handleDownloadPDF(r)}
                          aria-label="Download report as PDF">
                          <Download size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Report viewer */}
      {viewingReport && (
        <div className="pi-card" style={{ marginTop: 16 }}>
          <div className="pi-card-header">
            <span className="pi-card-title">{viewingReport.period} Report — {STATUS_CONFIG[viewingReport.status]?.label}</span>
            <div style={{ display: 'flex', gap: 8 }}>
              {editingNarrative !== viewingReport.id && canCreate && (
                <button className="pi-btn-ghost" style={{ fontSize: 11 }}
                  onClick={() => { setEditingNarrative(viewingReport.id); setEditedText(viewingReport.narrative || ''); }}
                  aria-label="Edit report narrative">
                  <Edit2 size={12} /> Edit
                </button>
              )}
              {editingNarrative === viewingReport.id && (
                <>
                  <button className="pi-btn-ghost" style={{ fontSize: 11 }} onClick={() => setEditingNarrative(null)}>Cancel</button>
                  <button className="pi-btn-primary" style={{ fontSize: 11 }} onClick={() => handleSaveEdit(viewingReport.id)}>Save</button>
                </>
              )}
            </div>
          </div>
          <div className="pi-card-body">
            {editingNarrative === viewingReport.id ? (
              <textarea value={editedText} onChange={e => setEditedText(e.target.value)} rows={12}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13.5, lineHeight: 1.7, resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }} />
            ) : (
              <p style={{ fontSize: 13.5, color: '#334155', lineHeight: 1.75, margin: 0, whiteSpace: 'pre-wrap' }}>{viewingReport.narrative}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
